/* @moldura padrao */
/**
 * A NATUREZA DE CADA ABA — `data-natureza`, decidido em 25/08/2026.
 *
 * Esta tela tem DEZ abas, e elas não são a mesma coisa: montar a OS é trabalho
 * de escritório; o instalador registrando o que fez é trabalho de campo. Até
 * hoje nada no código dizia qual era qual — então quem ia mexer numa aba não
 * sabia se precisava testá-la em 390px, e "bloquear a OS no celular" não tinha
 * como ser decidido sem tirar o instalador do app junto.
 *
 * O vocabulário é o da decisão 0 do visual, não um inventado agora:
 *
 *   composicao — criar e editar registro rico. Nasce no notebook.
 *   campo      — a rotina de quem está fazendo o serviço. Nasce no celular.
 *   consulta   — olhar, aprovar, responder. Tem de servir em qualquer aparelho.
 *
 * ⚠️ CLASSIFICA A ATIVIDADE, NÃO O LUGAR NEM A PESSOA. O instalador sentado no
 * escritório continua fazendo trabalho de CAMPO — registrar o que fez é campo
 * onde quer que ele esteja. Foi por isso que "rua"/"escritório" foi recusado:
 * *"o instalador tá lá no escritório sentado"* (Vittorio, 25/08).
 *
 * Para listar as partes de campo do app inteiro:
 *     grep -rn 'data-natureza="campo"' frontend/src
 *
 * A regra está em `11-convencao-visual.md` §7. Aqui fica só a marca.
 */
/**
 * O "de → para" de um evento de edição, lido do `js_dados`.
 *
 * Só desenha o que a linha traz: evento antigo (antes de 24/08) não tem nada, e
 * some sem quebrar. Mudança de CONTAGEM (produtos, ambientes) sai como número,
 * porque "de 2 para 3 produtos" é o que se quer saber — a lista inteira seria
 * ruído numa timeline.
 */
function DiffDoEvento({ dados }: { dados?: Record<string, unknown> | null }) {
  if (!dados || typeof dados !== 'object') return null;
  const linhas = Object.entries(dados).filter(
    ([, v]) => v && typeof v === 'object' && 'de' in (v as object),
  );
  if (linhas.length === 0) return null;

  const mostrar = (v: unknown) =>
    v === null || v === undefined || v === '' ? '—' : String(v);

  return (
    <ul className="list-unstyled small text-secondary mb-1 mt-1">
      {linhas.map(([campo, v]) => {
        const par = v as { de?: unknown; para?: unknown; contagem?: boolean };
        return (
          <li key={campo}>
            <span className="text-body-secondary">{campo.replace(/_/g, ' ')}:</span>{' '}
            <s>{mostrar(par.de)}</s> <i className="bi bi-arrow-right"></i>{' '}
            <strong className="text-body">{mostrar(par.para)}</strong>
            {par.contagem ? ' (quantidade)' : ''}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * form.tsx (ordens-servico) — Criar/editar OS (cliente, endereço, itens, visitas).
 * Tema AdminUX (Bootstrap 5 + react-bootstrap).
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import { CustomAutoTextarea } from '@/components/CustomAutoTextarea';
import { CustomCampoNumero } from '@/components/CustomCamposNumero';
import { doBanco, paraConta } from '@/lib/numero';
import { CustomCampoData } from '@/components/CustomCamposData';
import { CustomPainelAcessosOS } from './CustomPainelAcessosOS';
import { CustomCampoTelefone } from '@/components/CustomCampoTelefone';
import type { FormEvent, ReactNode } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Modal, Tab, Tabs } from 'react-bootstrap';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { comprimirImagem } from '@/lib/imagem';
import { formatarDataHora, formatarDiaMes } from '@/lib/data';
import { useAuth } from '@/auth/auth-context';
import type {
  Cobranca,
  EventoOS,
  ExecucaoEntry,
  Loja,
  OSAmbiente,
  OSDetalhe,
  OSFoto,
  OSItem,
  OSNota,
  OSRecurso,
  OSMedida,
  OSVisita,
  TipoOS,
  StatusOS,
} from './shared';
import {
  COBRANCAS,
  STATUS,
  STATUS_VISITA,
  TIPOS,
  caminhoDaLoja,
} from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { CustomMensagensBadge, CustomMensagensDoItem } from '@/components/CustomMensagens';

// Variantes semânticas do shared -> classes de badge do Bootstrap.
function badge(v: string): string {
  const mapa: Record<string, string> = {
    primary: 'text-bg-primary',
    info: 'text-bg-info',
    success: 'text-bg-success',
    warning: 'text-bg-warning',
    destructive: 'text-bg-danger',
    secondary: 'text-bg-secondary',
  };
  return mapa[v] ?? 'text-bg-secondary';
}

// Autorizações da OS vêm do módulo geral (/api/autorizacoes,
// entidade='ordens_servico' — ver migration 094).
interface AutOS {
  id: number;
  titulo: string;
  status: string;
  tipo_nome?: string | null;
  qtd_mensagens: number;
}

const ST_AUT: Record<string, { label: string; variante: string }> = {
  pendente: { label: 'Pendente', variante: 'warning' },
  autorizada: { label: 'Autorizada', variante: 'success' },
  negada: { label: 'Negada', variante: 'destructive' },
  cancelada: { label: 'Cancelada', variante: 'secondary' },
};

interface PedidoImportadoResp {
  pedido_venda: string;
  cliente_nome: string;
  cliente_documento: string | null;
  cliente_telefone: string | null;
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
  obs_tiny: string | null;
  obs_interna_tiny: string | null;
  responsavel_id: number | null;
  vendedor: string | null;
  itens: OSItem[];
}

interface FormState {
  loja_id: string;
  tipo: TipoOS;
  status: StatusOS;
  dt_abertura: string; // 'YYYY-MM-DD' — data DA OS (editável, pode ser retroativa)
  cliente_nome: string;
  cliente_telefone: string;
  cliente_whatsapp: string;
  cliente_email: string;
  cliente_documento: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  referencia: string;
  pedido_venda: string;
  vendedor: string; // nome do vendedor (texto, vem do Tiny)
  responsavel_sel: string; // '' (nenhum) | id do usuário responsável
  arquiteto: string;
  observacoes: string;
  cliente_obs: string;
  cliente_obs_pdf: boolean;
  obs_tiny: string;
  obs_interna_tiny: string;
  cobranca: Cobranca;
  justificativa_cobranca: string;
  sem_pedido: boolean;
  origem: string;
  pv_travado: boolean;
}


const VAZIO: FormState = {
  loja_id: '',
  tipo: 'instalacao',
  status: 'aberta',
  dt_abertura: '',
  cliente_nome: '',
  cliente_telefone: '',
  cliente_whatsapp: '',
  cliente_email: '',
  cliente_documento: '',
  endereco: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
  referencia: '',
  pedido_venda: '',
  vendedor: '',
  responsavel_sel: '',
  arquiteto: '',
  observacoes: '',
  cliente_obs: '',
  cliente_obs_pdf: false,
  obs_tiny: '',
  obs_interna_tiny: '',
  cobranca: 'a_definir',
  justificativa_cobranca: '',
  sem_pedido: false,
  origem: '',
  pv_travado: false,
};

/** Hoje em 'YYYY-MM-DD' pelo relógio LOCAL (toISOString daria UTC e viraria o dia à noite). */
function hojeIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Máscara de CPF (000.000.000-00) ou CNPJ (00.000.000/0000-00) conforme o tamanho. */
function mascaraCpfCnpj(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return d
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/** Normaliza um telefone (ex.: vindo do Tiny, sem DDI) para E.164 BR (+55…). */
function telefoneParaE164(v?: string | null): string {
  const d = (v ?? '').replace(/\D/g, '');
  if (!d) return '';
  // 12-13 dígitos começando com 55 já tem DDI; 10-11 é DDD+número (BR sem DDI).
  if (d.length >= 12 && d.startsWith('55')) return `+${d}`;
  return `+55${d}`;
}

interface OpcaoUsuario {
  id: number;
  nome: string;
}

export function OrdemServicoFormPage() {
  const { id } = useParams();
  const edicao = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  /** Pra onde "Voltar" leva. Quem abre a OS pode dizer de onde veio
   *  (`navigate(..., { state: { voltarPara } })`) — a Agenda faz isso pra devolver
   *  a pessoa ao calendário. Sem isso, volta pra lista, como sempre foi. */
  const voltarPara =
    (location.state as { voltarPara?: string } | null)?.voltarPara ??
    '/ordens-servico';
  const [searchParams] = useSearchParams();
  const { usuario, pode: podeEmAlgumLugar } = useAuth();

  /**
   * O QUE A PESSOA PODE NESTA OS — não "em algum lugar".
   *
   * Desde a migration 126 o acesso tem âmbito: ver Ambientes em Trancoso não é
   * ver em Búzios, e o instalador a quem deram UMA OS de outra loja pode nela o
   * pacote que recebeu. O `/me` responde a união de tudo; quem responde por
   * esta OS é o próprio detalhe (`permissoes`). Ref + estado: a ref vale já na
   * linha seguinte ao carregar (as chamadas das abas), o estado redesenha.
   * Permissão de outro módulo (agenda, autorizações) segue pelo `/me`.
   */
  const permsDaOSRef = useRef<Set<string> | null>(null);
  const [, setPermsDaOS] = useState<Set<string> | null>(null);
  const pode = (permissao: string) =>
    permsDaOSRef.current && permissao.startsWith('ordens-servico.')
      ? permsDaOSRef.current.has(permissao)
      : podeEmAlgumLugar(permissao);

  /**
   * CADA ABA TEM AS SUAS PERMISSÕES — `ver` e `editar`, separadas.
   *
   * Sem `ver`, a aba não aparece. Com `ver` e sem `editar`, ela aparece e não
   * se mexe: o conteúdo fica dentro de um `<fieldset disabled>`, que é HTML
   * nativo e desabilita tudo o que está dentro — em vez de espalhar `disabled`
   * por duzentos campos e esquecer de três.
   *
   * O pedido que criou isto (19/08/2026): o instalador entra na OS e vai
   * direto para a Execução, que é onde ele põe informação. O resto ele pode
   * ver — *"não é que seja sigiloso"* —, mas não mexe.
   */
  const podeVerAba = (aba: string) => pode(`ordens-servico.${aba}.ver`);
  const podeEditarAba = (aba: string) => pode(`ordens-servico.${aba}.editar`);

  // A aba que abre: a pedida na URL, se a pessoa alcança; senão a primeira que
  // ela pode ver. Quem só tem Execução cai na Execução sem precisar procurar.
  const ORDEM_ABAS = [
    'dados', 'produtos', 'roteiro', 'ambientes', 'visitas',
    'execucao', 'autorizacao', 'chat', 'atividades', 'observacoes',
  ];
  // Quatro abas não são recursos da OS: elas vivem em outro módulo (Visitas é
  // da Agenda, Autorização é do módulo de Autorizações) ou já tinham recurso
  // próprio (Execução, Chat, Atividades). A permissão delas é a de lá — repetir
  // aqui uma `ordens-servico.visitas.ver` seria inventar uma segunda verdade
  // sobre o mesmo acesso.
  const abaVisivel = (aba: string) => {
    if (aba === 'execucao') return pode('ordens-servico.execucao.ver');
    if (aba === 'atividades') return pode('ordens-servico.atividades.ver');
    if (aba === 'chat') return pode('ordens-servico.chat.ver');
    if (aba === 'visitas') return pode('agenda.visitas.ver');
    if (aba === 'autorizacao') return pode('autorizacoes.pedido.ver');
    return podeVerAba(aba);
  };
  const pedida = searchParams.get('tab');
  const abaInicial =
    pedida && abaVisivel(pedida)
      ? pedida
      : (ORDEM_ABAS.find(abaVisivel) ?? 'dados');

  const [lojas, setLojas] = useState<Loja[]>([]);
  const [form, setForm] = useState<FormState>(VAZIO);
  const [itens, setItens] = useState<OSItem[]>([]);
  const [visitas, setVisitas] = useState<OSVisita[]>([]);
  const [ambientes, setAmbientes] = useState<OSAmbiente[]>([]);
  const [notas, setNotas] = useState<OSNota[]>([]);
  const [recursos, setRecursos] = useState<OSRecurso[]>([]);
  const [catalogoRecursos, setCatalogoRecursos] = useState<
    { id: number; nome: string }[]
  >([]);
  const [novoRecurso, setNovoRecurso] = useState('');
  const [codigo, setCodigo] = useState<string | null>(null);
  // Carimbo de cadastro (imutável). Só aparece quando difere da data da OS.
  const [dtCriacao, setDtCriacao] = useState<string | null>(null);
  const [arquivada, setArquivada] = useState(false); // Fatia 5b (reversível)
  const [carregando, setCarregando] = useState(true);
  // A OS que não abriu (403/404). Antes a tela seguia com o formulário vazio e
  // o título "OS null" — o ensaio de 29/10/2026 pegou a Jhennifer numa OS de BZ.
  const [semAcesso, setSemAcesso] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [opcoesUsuarios, setOpcoesUsuarios] = useState<OpcaoUsuario[]>([]);
  const [jaPuxou, setJaPuxou] = useState(false);
  const [confirmarPv, setConfirmarPv] = useState(false);
  // Chave "ai:mi" da medida cujo upload está em andamento.
  const [uploadingMedida, setUploadingMedida] = useState<string | null>(null);
  // O seletor de foto das medidas mora FORA do <fieldset> da aba: quem só
  // fotografa (o instalador) tem a aba travada, e um input dentro de fieldset
  // desabilitado não abre — era o "toco e nada acontece" de 16/10/2026.
  const seletorFotoMedida = useRef<HTMLInputElement>(null);
  const alvoFotoMedida = useRef<{ ai: number; mi: number } | null>(null);
  // URL da imagem aberta no visualizador (lightbox); null = fechado.
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [cobrancaInfo, setCobrancaInfo] = useState<{
    por: string | null;
    dt: string | null;
  }>({ por: null, dt: null });

  // Autorizações desta OS (módulo geral; vinculadas via entidade='os').
  const [autsOS, setAutsOS] = useState<AutOS[]>([]);
  const [codigoOS, setCodigoOS] = useState('');

  // A CONVERSA DA OS É O MOTOR ÚNICO desde 04/09/2026 (fatia 3b da ficha #10):
  // a aba Chat é o `CustomMensagensDoItem` ancorado em ('ordens_servico', id).
  // Saíram com o chat velho: a lista de mensagens, os anexos, a menção escrita
  // à mão (`renderComMencoes`), o polling e o contador de não lidas — tudo isso
  // agora é do componente, e igual em toda tela que pendurar uma sala.
  const [aba, setAba] = useState(abaInicial);

  // Timeline de atividades
  const [atividades, setAtividades] = useState<EventoOS[]>([]);

  // Execução do instalador
  const [execucoes, setExecucoes] = useState<ExecucaoEntry[]>([]);
  const [execTexto, setExecTexto] = useState('');
  const [execFotos, setExecFotos] = useState<OSFoto[]>([]);
  const [enviandoExec, setEnviandoExec] = useState(false);
  const [uploadingExec, setUploadingExec] = useState(false);
  const [execTravada, setExecTravada] = useState(false);
  const [travaInfo, setTravaInfo] = useState<{ por: string | null; dt: string | null }>({
    por: null,
    dt: null,
  });
  const [travando, setTravando] = useState(false);

  // Visitas (agenda) — só consulta aqui; marcar e mudar é na Agenda (29/10/2026).
  /** Linhas abertas da tabela de visitas (por id; negativo = ainda sem id). */
  const [visitasAbertas, setVisitasAbertas] = useState<Set<number>>(new Set());
  function toggleVisitaAberta(chave: number) {
    setVisitasAbertas((s) => {
      const novo = new Set(s);
      if (!novo.delete(chave)) novo.add(chave);
      return novo;
    });
  }

  // Quem destrava é quem passa por cima da trava e apaga lançamento alheio —
  // a mesma regra do backend. Era `perfil === 'gestor'` até 26/08/2026, quando
  // o perfil `gestor` virou Gestor Administrativo e Gestor Financeiro.
  const podeDestravarExec = pode('ordens-servico.execucao.travar');
  const podeEscreverExec = !execTravada || podeDestravarExec;

  async function carregarAutsOS() {
    if (!id) return;
    try {
      setAutsOS(
        await api.get<AutOS[]>(
          // `ordens_servico`, não `os` (094): era assim que a aba vinha vazia
          // mesmo com autorizações vinculadas — o banco falava outra língua.
          `/autorizacoes?entidade_tipo=ordens_servico&entidade_id=${id}`,
        ),
      );
    } catch {
      /* silencioso */
    }
  }

  // ─── Execução ──────────────────────────────────────────────────────────
  async function carregarExecucao() {
    if (!id) return;
    try {
      setExecucoes(await api.get<ExecucaoEntry[]>(`/ordens-servico/${id}/execucao`));
    } catch {
      /* silencioso */
    }
  }

  async function carregarAtividades() {
    if (!id) return;
    try {
      setAtividades(await api.get<EventoOS[]>(`/ordens-servico/${id}/atividades`));
    } catch {
      /* silencioso */
    }
  }

  async function uploadExecFotos(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadingExec(true);
    try {
      for (const file of Array.from(files)) {
        const enviado = await comprimirImagem(file);
        const fd = new FormData();
        fd.append('arquivo', enviado);
        const foto = await api.upload<{ arquivo: string; nome: string }>(
          '/ordens-servico/fotos',
          fd,
        );
        setExecFotos((arr) => [...arr, { arquivo: foto.arquivo, nome: foto.nome }]);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao enviar a foto.');
    } finally {
      setUploadingExec(false);
    }
  }

  async function enviarExecucao() {
    if (!id) return;
    if (!execTexto.trim() && execFotos.length === 0) {
      toast.error('Escreva algo ou anexe ao menos uma foto.');
      return;
    }
    setEnviandoExec(true);
    try {
      const e = await api.post<ExecucaoEntry>(`/ordens-servico/${id}/execucao`, {
        texto: execTexto.trim() || null,
        fotos: execFotos,
      });
      setExecucoes((arr) => [...arr, e]);
      setExecTexto('');
      setExecFotos([]);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setEnviandoExec(false);
    }
  }

  async function apagarExecucao(entryId: number) {
    try {
      await api.delete(`/ordens-servico/${id}/execucao/${entryId}`);
      setExecucoes((arr) => arr.filter((e) => e.id !== entryId));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível apagar.');
    }
  }

  /* ARQUIVAR E TRAVAR PEDEM CONFIRMAÇÃO (29/10/2026). Um clique sem querer
     tirava a OS das listas ou fechava a execução para os instaladores —
     o ensaio pegou os dois. Desarquivar e destravar não pedem: desfazem. */
  const [confirmar, setConfirmar] = useState<null | 'arquivar' | 'travar'>(null);

  async function alternarTrava() {
    if (!id) return;
    setTravando(true);
    try {
      const os = await api.post<OSDetalhe>(`/ordens-servico/${id}/execucao/travar`, {
        travar: !execTravada,
      });
      setExecTravada(Boolean(os.execucao_travada));
      setTravaInfo({ por: os.trava_por ?? null, dt: os.dt_trava ?? null });
      toast.success(os.execucao_travada ? 'Execução travada.' : 'Execução destravada.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível alterar a trava.');
    } finally {
      setTravando(false);
    }
  }

  function set<K extends keyof FormState>(campo: K, valor: FormState[K]) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  useEffect(() => {
    async function init() {
      setCarregando(true);
      try {
        const [ls, ops, cat] = await Promise.all([
          api.get<Loja[]>('/lojas'),
          api.get<OpcaoUsuario[]>('/usuarios/opcoes').catch(() => []),
          api.get<{ id: number; nome: string }[]>('/ordens-servico/itens-a-levar').catch(() => []),
        ]);
        // Na Nova OS, só as lojas onde a pessoa cria: a Jhennifer via Búzios,
        // preenchia tudo e só levava o 403 no Criar (ensaio, rodada 2). Na
        // edição, todas — a OS pode ser de outra loja e o campo mostra qual é.
        const ondeCria = new Set((usuario?.lojas_criar_os ?? []).map((l) => l.id));
        setLojas(edicao ? ls : ls.filter((l) => ondeCria.has(l.id)));
        setOpcoesUsuarios(ops);
        setCatalogoRecursos(cat);

        if (edicao) {
          const os = await api.get<OSDetalhe>(`/ordens-servico/${id}`);
          aplicarOS(os);
          // Só busca o que a pessoa pode ver: sem a permissão, as abas nem
          // aparecem, e as duas chamadas eram 403 calados a cada OS aberta.
          if (abaVisivel('execucao')) carregarExecucao();
          if (abaVisivel('atividades')) carregarAtividades();
        } else {
          // A loja da página de onde a pessoa veio (`?loja=tr`, do "Nova OS"
          // da lista daquela loja); sem ela, a loja padrão (ou a primeira).
          const podeCriar = ls.filter((l) => ondeCria.has(l.id));
          const daPagina = podeCriar.find(
            (l) => l.codigo.toLowerCase() === searchParams.get('loja')?.toLowerCase(),
          );
          const padraoDela = podeCriar.find((l) => l.id === usuario?.loja_id)?.id;
          const padrao = daPagina?.id ?? padraoDela ?? podeCriar[0]?.id;
          // O responsável NÃO vem preenchido: quem cria é a gestão, e quem ela
          // escolhe é o vendedor da OS (dele, 29/10/2026). Pré-preencher com
          // quem cria punha a Suany como responsável de tudo.
          setForm({
            ...VAZIO,
            loja_id: padrao ? String(padrao) : '',
            dt_abertura: hojeIso(), // padrão hoje, mas o usuário pode retroagir
          });
        }
      } catch (err) {
        if (edicao && err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          setSemAcesso(true);
        } else {
          toast.error(
            err instanceof ApiError ? err.message : 'Erro ao carregar a OS.',
          );
        }
      } finally {
        setCarregando(false);
      }
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (aba === 'atividades' && id && abaVisivel('atividades')) carregarAtividades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aba]);

  function addItem() {
    setItens((arr) => [...arr, { descricao: '', quantidade: 1, observacao: '' }]);
  }
  function setItem(i: number, campo: keyof OSItem, valor: string | number) {
    setItens((arr) =>
      arr.map((it, idx) => (idx === i ? { ...it, [campo]: valor } : it)),
    );
  }
  function rmItem(i: number) {
    setItens((arr) => arr.filter((_, idx) => idx !== i));
  }

  // ── Roteiro: notas fiscais (linhas) ──
  function addNota() {
    setNotas((arr) => [
      ...arr,
      { fornecedor: '', numero: '', volumes: null, conteudo: '' },
    ]);
  }
  function setNota(i: number, campo: keyof OSNota, valor: string | number | null) {
    setNotas((arr) => arr.map((n, idx) => (idx === i ? { ...n, [campo]: valor } : n)));
  }
  function rmNota(i: number) {
    setNotas((arr) => arr.filter((_, idx) => idx !== i));
  }

  // ── Roteiro: "o que levar" (catálogo reutilizável) ──
  function addRecurso(nome: string) {
    const nm = nome.trim();
    if (!nm) return;
    if (recursos.some((r) => r.nome.toLowerCase() === nm.toLowerCase())) {
      setNovoRecurso(''); // já está na lista
      return;
    }
    // Se existe no catálogo, reusa o id; senão vira novo (backend cria ao salvar).
    const doCatalogo = catalogoRecursos.find(
      (c) => c.nome.toLowerCase() === nm.toLowerCase(),
    );
    setRecursos((arr) => [
      ...arr,
      doCatalogo
        ? { ...doCatalogo, quantidade: null, observacao: '' }
        : { nome: nm, quantidade: null, observacao: '' },
    ]);
    setNovoRecurso('');
  }
  function setRecurso(
    i: number,
    campo: keyof OSRecurso,
    valor: string | number | null,
  ) {
    setRecursos((arr) =>
      arr.map((r, idx) => (idx === i ? { ...r, [campo]: valor } : r)),
    );
  }
  function rmRecurso(i: number) {
    setRecursos((arr) => arr.filter((_, idx) => idx !== i));
  }

  // Popula o estado do formulário a partir de uma OS vinda da API. Usado no
  // carregamento inicial e após salvar — assim continuamos NA OS (sem sair).
  function aplicarOS(os: OSDetalhe) {
    // Só o GET do detalhe traz as permissões; a resposta de quem SALVA não —
    // então só se troca quando vierem.
    if (os.permissoes?.length) {
      permsDaOSRef.current = new Set(os.permissoes);
      setPermsDaOS(permsDaOSRef.current);
    }
    setCodigo(os.codigo);
    setArquivada(os.arquivada ?? false);
    setForm({
      loja_id: String(os.loja_id),
      tipo: os.tipo,
      status: os.status,
      dt_abertura: (os.dt_abertura ?? '').slice(0, 10),
      cliente_nome: os.cliente_nome ?? '',
      cliente_telefone: os.cliente_telefone ?? '',
      cliente_whatsapp: os.cliente_whatsapp ?? '',
      cliente_email: os.cliente_email ?? '',
      cliente_documento: os.cliente_documento ?? '',
      endereco: os.endereco ?? '',
      numero: os.numero ?? '',
      complemento: os.complemento ?? '',
      bairro: os.bairro ?? '',
      cidade: os.cidade ?? '',
      uf: os.uf ?? '',
      cep: os.cep ?? '',
      referencia: os.referencia ?? '',
      pedido_venda: os.pedido_venda ?? '',
      vendedor: os.vendedor ?? '',
      responsavel_sel: os.responsavel_id ? String(os.responsavel_id) : '',
      arquiteto: os.arquiteto ?? '',
      observacoes: os.observacoes ?? '',
      cliente_obs: os.cliente_obs ?? '',
      cliente_obs_pdf: os.cliente_obs_pdf ?? false,
      obs_tiny: os.obs_tiny ?? '',
      obs_interna_tiny: os.obs_interna_tiny ?? '',
      cobranca: os.cobranca ?? 'a_definir',
      justificativa_cobranca: os.justificativa_cobranca ?? '',
      sem_pedido: os.sem_pedido ?? false,
      origem: os.origem ?? '',
      pv_travado: os.pv_travado ?? false,
    });
    setDtCriacao(os.dt_criacao ?? null);
    setCobrancaInfo({
      por: os.cobranca_definida_por ?? null,
      dt: os.dt_cobranca ?? null,
    });
    setCodigoOS(os.codigo);
    carregarAutsOS();
    setJaPuxou(Boolean(os.pedido_venda));
    setExecTravada(Boolean(os.execucao_travada));
    setTravaInfo({ por: os.trava_por ?? null, dt: os.dt_trava ?? null });
    setItens(os.itens ?? []);
    setVisitas(os.visitas ?? []);
    setAmbientes(normalizarAmbientes(os.ambientes ?? []));
    setNotas(os.notas ?? []);
    setRecursos(os.recursos ?? []);
  }

  // Migração graceful: OS antigas guardavam fotos no nível do ambiente.
  // Agora a foto é por medida — realocamos as fotos legadas para a 1ª medida
  // (criando uma medida vazia se o ambiente não tiver nenhuma) e garantimos
  // que toda medida tenha o array `fotos`.
  function normalizarAmbientes(lista: OSAmbiente[]): OSAmbiente[] {
    return lista.map((a) => {
      let medidas = (a.medidas ?? []).map((m) => ({
        ...m,
        fotos: m.fotos ?? [],
      }));
      const legado = a.fotos ?? [];
      if (legado.length > 0) {
        if (medidas.length === 0) {
          medidas = [
            {
              abertura: '',
              medida: '',
              modelo: '',
              fixacao: '',
              obs: '',
              fotos: [],
            },
          ];
        }
        medidas[0] = { ...medidas[0], fotos: [...medidas[0].fotos, ...legado] };
      }
      return { ...a, medidas, fotos: [] };
    });
  }

  function addAmbiente() {
    setAmbientes((arr) => [...arr, { descricao: '', obs: '', medidas: [] }]);
  }
  function setAmbiente(i: number, campo: 'descricao' | 'obs', valor: string) {
    setAmbientes((arr) =>
      arr.map((a, idx) => (idx === i ? { ...a, [campo]: valor } : a)),
    );
  }
  function rmAmbiente(i: number) {
    setAmbientes((arr) => arr.filter((_, idx) => idx !== i));
  }
  function addMedida(ai: number) {
    setAmbientes((arr) =>
      arr.map((a, idx) =>
        idx === ai
          ? {
              ...a,
              medidas: [
                ...a.medidas,
                {
                  abertura: '',
                  medida: '',
                  modelo: '',
                  fixacao: '',
                  obs: '',
                  fotos: [],
                },
              ],
            }
          : a,
      ),
    );
  }
  function setMedida(
    ai: number,
    mi: number,
    campo: keyof OSMedida,
    valor: string,
  ) {
    setAmbientes((arr) =>
      arr.map((a, idx) =>
        idx === ai
          ? {
              ...a,
              medidas: a.medidas.map((m, k) =>
                k === mi ? { ...m, [campo]: valor } : m,
              ),
            }
          : a,
      ),
    );
  }
  function rmMedida(ai: number, mi: number) {
    setAmbientes((arr) =>
      arr.map((a, idx) =>
        idx === ai
          ? { ...a, medidas: a.medidas.filter((_, k) => k !== mi) }
          : a,
      ),
    );
  }
  function rmFotoMedida(ai: number, mi: number, fi: number) {
    setAmbientes((arr) =>
      arr.map((a, idx) =>
        idx === ai
          ? {
              ...a,
              medidas: a.medidas.map((m, k) =>
                k === mi
                  ? { ...m, fotos: m.fotos.filter((_, j) => j !== fi) }
                  : m,
              ),
            }
          : a,
      ),
    );
  }
  async function uploadFotosMedida(
    ai: number,
    mi: number,
    files: FileList | null,
  ) {
    if (!files || files.length === 0) return;
    setUploadingMedida(`${ai}:${mi}`);
    try {
      for (const file of Array.from(files)) {
        const enviado = await comprimirImagem(file);
        const fd = new FormData();
        fd.append('arquivo', enviado);
        const foto = await api.upload<{ arquivo: string; nome: string }>(
          '/ordens-servico/fotos',
          fd,
        );
        if (!podeEditarAba('ambientes')) {
          // Quem só fotografa não tem "Salvar": a foto grava na hora, por
          // porta própria, e a aba volta do servidor.
          const ambienteId = ambientes[ai]?.id;
          if (!ambienteId) throw new Error('Recarregue a OS e tente de novo.');
          const r = await api.post<{ ambientes: OSAmbiente[] }>(
            `/ordens-servico/${id}/ambientes/${ambienteId}/medidas/${mi}/fotos`,
            { arquivo: foto.arquivo, nome: foto.nome },
          );
          setAmbientes(normalizarAmbientes(r.ambientes));
          continue;
        }
        setAmbientes((arr) =>
          arr.map((a, idx) =>
            idx === ai
              ? {
                  ...a,
                  medidas: a.medidas.map((m, k) =>
                    k === mi
                      ? {
                          ...m,
                          fotos: [
                            ...m.fotos,
                            { arquivo: foto.arquivo, nome: foto.nome },
                          ],
                        }
                      : m,
                  ),
                }
              : a,
          ),
        );
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Falha ao enviar a foto.',
      );
    } finally {
      setUploadingMedida(null);
    }
  }

  // Clique no botão: se já puxou antes, confirma (evita sobrescrever edições).
  function aoClicarPuxar() {
    if (!form.loja_id) {
      toast.error('Selecione a loja antes de puxar do ERP.');
      return;
    }
    if (!form.pedido_venda.trim()) {
      toast.error('Informe o nº do pedido de venda.');
      return;
    }
    if (jaPuxou) {
      setConfirmarPv(true);
      return;
    }
    executarPuxar();
  }

  async function executarPuxar() {
    setConfirmarPv(false);
    setImportando(true);
    try {
      const p = await api.get<PedidoImportadoResp>(
        `/ordens-servico/pedidos-tiny/${encodeURIComponent(
          form.pedido_venda.trim(),
        )}?loja_id=${form.loja_id}`,
      );
      setForm((f) => ({
        ...f,
        pedido_venda: p.pedido_venda ?? f.pedido_venda,
        cliente_nome: p.cliente_nome ?? f.cliente_nome,
        cliente_documento: p.cliente_documento ?? f.cliente_documento,
        cliente_telefone: p.cliente_telefone
          ? telefoneParaE164(p.cliente_telefone)
          : f.cliente_telefone,
        endereco: p.endereco ?? f.endereco,
        numero: p.numero ?? f.numero,
        complemento: p.complemento ?? f.complemento,
        bairro: p.bairro ?? f.bairro,
        cidade: p.cidade ?? f.cidade,
        uf: p.uf ?? f.uf,
        cep: p.cep ?? f.cep,
        obs_tiny: p.obs_tiny ?? f.obs_tiny,
        obs_interna_tiny: p.obs_interna_tiny ?? f.obs_interna_tiny,
        // vendedor (texto) vem do Tiny; responsável é sugerido casando com um usuário
        vendedor: p.vendedor ?? f.vendedor,
        responsavel_sel: p.responsavel_id ? String(p.responsavel_id) : f.responsavel_sel,
      }));
      if (p.itens?.length) {
        setItens(
          p.itens.map((it) => ({
            descricao: it.descricao,
            quantidade: it.quantidade,
            observacao: it.observacao ?? '',
          })),
        );
      }
      setJaPuxou(true);
      toast.success(`Pedido ${p.pedido_venda} importado do Tiny.`);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível puxar do ERP.',
      );
    } finally {
      setImportando(false);
    }
  }

  // Arquivar / desarquivar a OS (Fatia 5b). Reversível, ortogonal ao status: "encerrou
  // de vez" → some das listas e trava os eventos na agenda; desarquivar destrava.
  async function arquivarOS() {
    if (!id) return;
    try {
      const os = await api.post<OSDetalhe>(`/ordens-servico/${id}/arquivar`, {});
      setArquivada(Boolean(os.arquivada));
      toast.success('OS arquivada. Some das listas e trava na agenda.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Falha ao arquivar.');
    }
  }
  async function desarquivarOS() {
    if (!id) return;
    try {
      const os = await api.post<OSDetalhe>(`/ordens-servico/${id}/desarquivar`, {});
      setArquivada(Boolean(os.arquivada));
      toast.success('OS desarquivada.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Falha ao desarquivar.');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSalvando(true);
    if (form.cobranca === 'nao' && !form.justificativa_cobranca.trim()) {
      toast.error('Informe a justificativa quando a OS não tem cobrança.');
      setSalvando(false);
      return;
    }
    const payload = {
      ...form,
      loja_id: Number(form.loja_id),
      responsavel_id: form.responsavel_sel ? Number(form.responsavel_sel) : null,
      vendedor: form.vendedor.trim() || null,
      arquiteto: form.arquiteto.trim() || null,
      itens: itens
        .filter((it) => it.descricao.trim())
        .map((it) => ({
          descricao: it.descricao,
          quantidade: Number(it.quantidade) || 1,
          observacao: it.observacao || null,
        })),
      ambientes: ambientes
        .filter(
          (a) =>
            (a.descricao && a.descricao.trim()) || a.medidas.length > 0,
        )
        .map((a) => ({
          descricao: a.descricao || null,
          obs: a.obs || null,
          medidas: a.medidas.map((m) => ({
            abertura: m.abertura || null,
            medida: m.medida || null,
            modelo: m.modelo || null,
            fixacao: m.fixacao || null,
            obs: m.obs || null,
            fotos: m.fotos,
          })),
        })),
      notas: notas
        .filter(
          (n) =>
            n.fornecedor?.trim() ||
            n.numero?.trim() ||
            n.conteudo?.trim() ||
            n.volumes != null,
        )
        .map((n) => ({
          fornecedor: n.fornecedor?.trim() || null,
          numero: n.numero?.trim() || null,
          volumes: n.volumes != null ? Number(n.volumes) : null,
          conteudo: n.conteudo?.trim() || null,
        })),
      recursos: recursos.map((r) => ({
        ...(r.id ? { id: r.id } : { nome: r.nome }),
        quantidade: r.quantidade != null ? Number(r.quantidade) : null,
        observacao: r.observacao?.trim() || null,
      })),
    };
    try {
      if (edicao) {
        const os = await api.put<OSDetalhe>(`/ordens-servico/${id}`, payload);
        aplicarOS(os);
        carregarAtividades();
        toast.success('OS salva.');
      } else {
        const nova = await api.post<OSDetalhe>('/ordens-servico', payload);
        toast.success(`OS ${nova.codigo} criada.`);
        // Entra no modo edição da OS criada — continua na OS p/ seguir editando.
        navigate(`/ordens-servico/${nova.id}`, { replace: true });
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível salvar a OS.',
      );
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
        <span className="spinner-border spinner-border-sm" />
        Carregando…
      </div>
    );
  }

  if (semAcesso) {
    return (
      <CustomMolduraPagina
        titulo="OS indisponível"
        descricao="Instalações, manutenções e lavagens"
        caminho={[{ rotulo: 'Ordens de Serviço', para: '/ordens-servico' }]}
      >
        <div className="card adminuiux-card">
          <div className="card-body text-center py-5">
            <i className="bi bi-lock fs-1 text-secondary d-block mb-3" />
            <p className="mb-1 fw-semibold">Você não tem acesso a esta OS, ou ela não existe.</p>
            <p className="text-secondary small mb-4">
              Se precisa dela, peça a quem cuida da loja para liberar.
            </p>
            <Link to="/ordens-servico" className="btn btn-theme">
              Voltar às Ordens de Serviço
            </Link>
          </div>
        </div>
      </CustomMolduraPagina>
    );
  }

  // O breadcrumb é a ÁRVORE: a OS mora na página da loja dela.
  const lojaDaOS = lojas.find((l) => String(l.id) === form.loja_id);

  return (
    /* ⛔ O `voltarPara` DESTA TELA FOI O DEFEITO QUE ABRIU ESTA FRENTE.
       Só a Agenda o preenchia; quem chegava pela Inbox clicava em Voltar e caía
       na lista de OS, um lugar onde nunca esteve. O caminho de volta agora sai
       da TRILHA, na moldura, e vale para qualquer origem. */
    <CustomMolduraPagina
      titulo={edicao ? `OS ${codigo}` : 'Nova OS'}
      descricao="Instalações, manutenções e lavagens"
      caminho={[{ rotulo: 'Ordens de Serviço', para: lojaDaOS ? caminhoDaLoja(lojaDaOS.codigo) : '/ordens-servico' }]}
      acoes={
        edicao ? (
          <button
            type="button"
            className="btn btn-outline-theme"
            onClick={() => window.open(`/ordens-servico/${id}/imprimir`, '_blank')}
          >
            <i className="bi bi-printer me-1"></i> PDF
          </button>
        ) : undefined
      }
    >

      <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
        <Tabs
          activeKey={aba}
          onSelect={(k) => k && setAba(k)}
          variant="pills"
          className="mb-3 flex-wrap"
        >
          {/* natureza das abas: ver o bloco no topo do arquivo */}
          {abaVisivel('dados') && (
          <Tab data-natureza="composicao" /* montar a OS: cliente, endereço, responsável */ eventKey="dados" title="Dados & Cliente">
           {/* `fieldset disabled` desabilita TUDO o que está dentro — é HTML
               nativo, e não uma varredura de `disabled` campo a campo, que
               esqueceria três. Quem só tem `ver` lê a aba inteira e não muda
               nada. */}
           <fieldset disabled={!podeEditarAba('dados')}>
            {/* Dados da OS */}
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <h6 className="mb-3">
                  <i className="bi bi-clipboard-check text-theme-1 me-2"></i>
                  Dados da OS
                </h6>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <select
                        id="campo-loja"
                        className="form-select"
                        value={form.loja_id}
                        onChange={(e) => set('loja_id', e.target.value)}
                      >
                        <option value="">Selecione</option>
                        {lojas.map((l) => (
                          <option key={l.id} value={String(l.id)}>
                            {l.nome}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="campo-loja">Loja</label>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <select
                        id="campo-tipo"
                        className="form-select"
                        value={form.tipo}
                        onChange={(e) => set('tipo', e.target.value as TipoOS)}
                      >
                        {TIPOS.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="campo-tipo">Tipo</label>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <select
                        id="campo-status"
                        className="form-select"
                        value={form.status}
                        onChange={(e) => set('status', e.target.value as StatusOS)}
                      >
                        {STATUS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="campo-status">Status</label>
                    </div>
                  </div>
                  {/* Data DA OS: editável de propósito — OS lançada com atraso ou
                      recriada precisa entrar com a data que ela realmente tem.
                      Quem quiser saber quando foi CADASTRADA lê a linha abaixo. */}
                  <div className="col-12 col-md-4">
                    <CustomCampoData
                      id="campo-dt-abertura"
                      label="Data da OS"
                      valor={form.dt_abertura}
                      onChange={(iso) => set('dt_abertura', iso)}
                    />
                    {dtCriacao &&
                      dtCriacao.slice(0, 10) !== form.dt_abertura && (
                        <small className="text-secondary d-block mt-1">
                          <i className="bi bi-clock-history me-1"></i>
                          cadastrada em {formatarDataHora(dtCriacao)}
                        </small>
                      )}
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <input
                        id="campo-vendedor"
                        className="form-control"
                        placeholder="Vendedor"
                        value={form.vendedor}
                        onChange={(e) => set('vendedor', e.target.value)}
                      />
                      <label htmlFor="campo-vendedor">Vendedor</label>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <select
                        id="campo-responsavel"
                        className="form-select"
                        value={form.responsavel_sel}
                        onChange={(e) => set('responsavel_sel', e.target.value)}
                      >
                        <option value="">Selecione</option>
                        {opcoesUsuarios.map((u) => (
                          <option key={u.id} value={String(u.id)}>
                            {u.nome}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="campo-responsavel">Responsável</label>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <input
                        id="campo-arquiteto"
                        className="form-control"
                        placeholder="Nome do arquiteto"
                        value={form.arquiteto}
                        onChange={(e) => set('arquiteto', e.target.value)}
                      />
                      <label htmlFor="campo-arquiteto">Arquiteto (opcional)</label>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <select
                        id="campo-cobranca"
                        className="form-select"
                        value={form.cobranca}
                        onChange={(e) => set('cobranca', e.target.value as Cobranca)}
                      >
                        {COBRANCAS.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="campo-cobranca">Cobrança</label>
                    </div>
                  </div>

                  {form.cobranca === 'nao' && (
                    <div className="col-12">
                      <div className="form-floating">
                        <textarea
                          id="campo-just-cobranca"
                          className="form-control"
                          style={{ height: '80px' }}
                          placeholder="Ex.: cortesia, garantia, retrabalho nosso…"
                          value={form.justificativa_cobranca}
                          onChange={(e) =>
                            set('justificativa_cobranca', e.target.value)
                          }
                        />
                        <label htmlFor="campo-just-cobranca">
                          Justificativa (por que não há cobrança)
                        </label>
                      </div>
                    </div>
                  )}

                  {cobrancaInfo.por && (
                    <div className="col-12">
                      <p className="small text-secondary mb-0">
                        Cobrança definida por <strong>{cobrancaInfo.por}</strong>
                        {cobrancaInfo.dt
                          ? ` em ${formatarDataHora(cobrancaInfo.dt)}`
                          : ''}
                        .
                      </p>
                    </div>
                  )}

                  <div className="col-12">
                    <div className="form-check mb-2">
                      <input
                        id="campo-sem-pedido"
                        type="checkbox"
                        className="form-check-input"
                        checked={form.sem_pedido}
                        onChange={(e) => {
                          const v = e.target.checked;
                          setForm((f) => ({
                            ...f,
                            sem_pedido: v,
                            pedido_venda: v ? '' : f.pedido_venda,
                          }));
                        }}
                      />
                      <label className="form-check-label" htmlFor="campo-sem-pedido">
                        OS sem pedido de venda (não-cliente / outra revenda / venda antiga)
                      </label>
                    </div>

                    {form.sem_pedido ? (
                      <div className="form-floating">
                        <input
                          id="campo-origem"
                          className="form-control"
                          placeholder="De onde veio"
                          value={form.origem}
                          onChange={(e) => set('origem', e.target.value)}
                        />
                        <label htmlFor="campo-origem">
                          Origem (revenda, cliente antigo, etc.)
                        </label>
                      </div>
                    ) : (
                      <>
                        <div className="d-flex gap-2 align-items-start">
                          <div
                            className="form-floating"
                            style={{ flex: '1 1 20rem', maxWidth: '20rem' }}
                          >
                            <input
                              id="campo-pedido"
                              className="form-control"
                              placeholder="Ex.: 12345"
                              value={form.pedido_venda}
                              disabled={form.pv_travado}
                              onChange={(e) => set('pedido_venda', e.target.value)}
                            />
                            <label htmlFor="campo-pedido">Nº do pedido de venda</label>
                          </div>
                          <button
                            type="button"
                            className={`btn ${
                              form.pv_travado ? 'btn-theme' : 'btn-outline-secondary'
                            }`}
                            style={{ height: 'calc(3.5rem + 2px)' }}
                            onClick={() => set('pv_travado', !form.pv_travado)}
                            title={
                              form.pv_travado
                                ? 'Destravar o Puxar do ERP'
                                : 'Travar o Puxar do ERP (evita sobrescrever)'
                            }
                          >
                            <i
                              className={`bi ${
                                form.pv_travado ? 'bi-lock-fill' : 'bi-unlock'
                              }`}
                            ></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-theme"
                            style={{ height: 'calc(3.5rem + 2px)' }}
                            onClick={aoClicarPuxar}
                            disabled={importando || form.pv_travado}
                          >
                            {importando ? (
                              <span className="spinner-border spinner-border-sm me-1" />
                            ) : (
                              <i className="bi bi-cloud-download me-1"></i>
                            )}
                            Puxar do ERP
                          </button>
                        </div>
                        {form.pv_travado && (
                          <p className="small text-secondary mt-1 mb-0">
                            <i className="bi bi-lock-fill me-1"></i>
                            Puxar do ERP travado — os dados não serão sobrescritos.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="col-12">
                    <CustomAutoTextarea
                      id="campo-objeto"
                      label="Objeto do serviço (o que será feito)"
                      value={form.observacoes}
                      onChange={(val) => set('observacoes', val)}
                      placeholder="O que será feito nesta OS"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Cliente */}
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <h6 className="mb-3">
                  <i className="bi bi-person text-theme-1 me-2"></i>
                  Cliente
                </h6>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="form-floating">
                      <input
                        id="campo-cli-nome"
                        className="form-control"
                        placeholder="Nome"
                        value={form.cliente_nome}
                        onChange={(e) => set('cliente_nome', e.target.value)}
                        required
                      />
                      <label htmlFor="campo-cli-nome">Nome</label>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-floating">
                      <input
                        id="campo-cli-doc"
                        className="form-control"
                        placeholder="CPF / CNPJ"
                        value={form.cliente_documento}
                        onChange={(e) =>
                          set('cliente_documento', mascaraCpfCnpj(e.target.value))
                        }
                      />
                      <label htmlFor="campo-cli-doc">CPF / CNPJ</label>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <CustomCampoTelefone
                      id="campo-cli-tel"
                      label="Telefone"
                      value={form.cliente_telefone}
                      onChange={(v) => set('cliente_telefone', v)}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <CustomCampoTelefone
                      id="campo-cli-whats"
                      label="WhatsApp"
                      value={form.cliente_whatsapp}
                      onChange={(v) => set('cliente_whatsapp', v)}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <input
                        id="campo-cli-email"
                        type="email"
                        className="form-control"
                        placeholder="E-mail"
                        value={form.cliente_email}
                        onChange={(e) => set('cliente_email', e.target.value)}
                      />
                      <label htmlFor="campo-cli-email">E-mail</label>
                    </div>
                  </div>
                  <div className="col-12">
                    <CustomAutoTextarea
                      id="campo-cli-obs"
                      label="Anotações sobre o cliente"
                      value={form.cliente_obs}
                      onChange={(val) => set('cliente_obs', val)}
                      placeholder="Preferências, histórico, observações sobre o cliente"
                    />
                    <div className="form-check mt-2">
                      <input
                        id="campo-cli-obs-pdf"
                        type="checkbox"
                        className="form-check-input"
                        checked={form.cliente_obs_pdf}
                        onChange={(e) => set('cliente_obs_pdf', e.target.checked)}
                      />
                      <label
                        className="form-check-label small text-secondary"
                        htmlFor="campo-cli-obs-pdf"
                      >
                        Incluir estas anotações no PDF da OS
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <h6 className="mb-3">
                  <i className="bi bi-geo-alt text-theme-1 me-2"></i>
                  Endereço
                </h6>
                <div className="row g-3">
                  <div className="col-12 col-md-8">
                    <div className="form-floating">
                      <input
                        id="campo-end"
                        className="form-control"
                        placeholder="Logradouro"
                        value={form.endereco}
                        onChange={(e) => set('endereco', e.target.value)}
                      />
                      <label htmlFor="campo-end">Logradouro</label>
                    </div>
                  </div>
                  <div className="col-6 col-md-2">
                    <div className="form-floating">
                      <input
                        id="campo-num"
                        className="form-control"
                        placeholder="Número"
                        value={form.numero}
                        onChange={(e) => set('numero', e.target.value)}
                      />
                      <label htmlFor="campo-num">Número</label>
                    </div>
                  </div>
                  <div className="col-6 col-md-2">
                    <div className="form-floating">
                      <input
                        id="campo-compl"
                        className="form-control"
                        placeholder="Complemento"
                        value={form.complemento}
                        onChange={(e) => set('complemento', e.target.value)}
                      />
                      <label htmlFor="campo-compl">Complemento</label>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <input
                        id="campo-bairro"
                        className="form-control"
                        placeholder="Bairro"
                        value={form.bairro}
                        onChange={(e) => set('bairro', e.target.value)}
                      />
                      <label htmlFor="campo-bairro">Bairro</label>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-floating">
                      <input
                        id="campo-cidade"
                        className="form-control"
                        placeholder="Cidade"
                        value={form.cidade}
                        onChange={(e) => set('cidade', e.target.value)}
                      />
                      <label htmlFor="campo-cidade">Cidade</label>
                    </div>
                  </div>
                  <div className="col-6 col-md-2">
                    <div className="form-floating">
                      <input
                        id="campo-uf"
                        className="form-control"
                        placeholder="UF"
                        maxLength={2}
                        value={form.uf}
                        onChange={(e) => set('uf', e.target.value.toUpperCase())}
                      />
                      <label htmlFor="campo-uf">UF</label>
                    </div>
                  </div>
                  <div className="col-6 col-md-2">
                    <div className="form-floating">
                      <input
                        id="campo-cep"
                        className="form-control"
                        placeholder="CEP"
                        value={form.cep}
                        onChange={(e) => set('cep', e.target.value)}
                      />
                      <label htmlFor="campo-cep">CEP</label>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-floating">
                      <input
                        id="campo-ref"
                        className="form-control"
                        placeholder="Ponto de referência"
                        value={form.referencia}
                        onChange={(e) => set('referencia', e.target.value)}
                      />
                      <label htmlFor="campo-ref">Ponto de referência</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
           </fieldset>
           {/* FORA do fieldset: dar a OS a alguém não é editar os dados dela. */}
           {edicao && id && pode('ordens-servico.acessos.ver') && (
             <CustomPainelAcessosOS
               osId={Number(id)}
               /* A própria pessoa não se dá a OS (o servidor recusa). */
               pessoas={opcoesUsuarios.filter((o) => o.id !== usuario?.id)}
               podeGerir={pode('ordens-servico.acessos.gerir')}
             />
           )}
          </Tab>
          )}

          {abaVisivel('produtos') && (
          <Tab data-natureza="composicao" /* montar o que a OS leva */ eventKey="produtos" title="Produtos">
           <fieldset disabled={!podeEditarAba('produtos')}>
            {/* Itens */}
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">
                    <i className="bi bi-box text-theme-1 me-2"></i>
                    Produtos / Itens
                  </h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-theme"
                    onClick={addItem}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar item
                  </button>
                </div>
                <div className="d-flex flex-column gap-3">
                  {itens.length === 0 && (
                    <p className="small text-secondary mb-0">
                      Nenhum item. Clique em "Adicionar item".
                    </p>
                  )}
                  {itens.map((it, i) => (
                    <div key={i} className="d-flex gap-2 align-items-start">
                      <input
                        className="form-control flex-grow-1"
                        placeholder="Descrição do produto"
                        value={it.descricao}
                        onChange={(e) => setItem(i, 'descricao', e.target.value)}
                      />
                      {/* nr_quantidade e DECIMAL(10,2): a precisao varia por
                          linha (3 cortinas, 2,5 m), entao aqui a virgula continua
                          sendo digitada — rapida={false}, como era antes. */}
                      <CustomCampoNumero
                        rapida={false}
                        casas={2}
                        largura="6rem"
                        placeholder="Qtd"
                        valor={doBanco(it.quantidade, 2, 'os.item.quantidade')}
                        onChange={(v) => setItem(i, 'quantidade', paraConta(v) || 1)}
                      />
                      <input
                        className="form-control"
                        style={{ width: '12rem' }}
                        placeholder="Observação"
                        value={it.observacao ?? ''}
                        onChange={(e) => setItem(i, 'observacao', e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-link btn-square text-danger"
                        onClick={() => rmItem(i)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
           </fieldset>
          </Tab>
          )}

          {abaVisivel('roteiro') && (
          <Tab data-natureza="composicao" /* planejar as idas */ eventKey="roteiro" title="Roteiro">
           <fieldset disabled={!podeEditarAba('roteiro')}>
            {/* Notas fiscais (do fornecedor) — NF + volumes, em linhas */}
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">
                    <i className="bi bi-receipt text-theme-1 me-2"></i>
                    Notas fiscais (fornecedor)
                    {notas.reduce((s, n) => s + (Number(n.volumes) || 0), 0) > 0 && (
                      <span className="badge bg-theme-1-subtle text-theme-1 ms-2">
                        {notas.reduce((s, n) => s + (Number(n.volumes) || 0), 0)}{' '}
                        volumes
                      </span>
                    )}
                  </h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-theme"
                    onClick={addNota}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar NF
                  </button>
                </div>
                <div className="d-flex flex-column gap-2">
                  {notas.length === 0 && (
                    <p className="small text-secondary mb-0">
                      Nenhuma nota. Clique em "Adicionar NF".
                    </p>
                  )}
                  {notas.map((n, i) => (
                    <div key={i} className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-receipt text-theme-1"></i>
                      </span>
                      <input
                        className="form-control"
                        placeholder="Fornecedor (ex.: Hunter Douglas)"
                        value={n.fornecedor ?? ''}
                        onChange={(e) => setNota(i, 'fornecedor', e.target.value)}
                      />
                      <input
                        className="form-control flex-grow-0"
                        style={{ width: '9rem' }}
                        placeholder="Nº da NF"
                        value={n.numero ?? ''}
                        onChange={(e) => setNota(i, 'numero', e.target.value)}
                      />
                      {/* nr_volumes e INT: casas={0} da o inteiro com milhar */}
                      <CustomCampoNumero
                        casas={0}
                        largura="6.5rem"
                        placeholder="Volumes"
                        valor={n.volumes != null ? String(n.volumes) : ''}
                        onChange={(v) =>
                          setNota(i, 'volumes', v === '' ? null : paraConta(v))
                        }
                      />
                      <input
                        className="form-control"
                        placeholder="O que tem (opcional)"
                        value={n.conteudo ?? ''}
                        onChange={(e) => setNota(i, 'conteudo', e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        aria-label="Remover nota"
                        onClick={() => rmNota(i)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* O que levar — lista (item por linha) com catálogo reutilizável */}
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <h6 className="mb-2">
                  <i className="bi bi-tools text-theme-1 me-2"></i>
                  O que levar
                </h6>
                <p className="small text-secondary mb-3">
                  Escada, andaime, parafusadeira… Cada item vira uma linha, com
                  quantidade e observação opcionais. Se o item ainda não existe, ele
                  fica salvo no catálogo pra reusar na próxima OS.
                </p>

                {/* Lista dos itens desta OS — cada linha é um input-group */}
                <div className="d-flex flex-column gap-2 mb-3">
                  {recursos.length === 0 && (
                    <p className="small text-secondary mb-0">
                      Nenhum item ainda. Adicione abaixo.
                    </p>
                  )}
                  {recursos.map((r, i) => (
                    <div key={i} className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-tools text-theme-1 me-2"></i>
                        {r.nome}
                      </span>
                      {/* nr_quantidade do recurso e INT */}
                      <CustomCampoNumero
                        casas={0}
                        largura="5.5rem"
                        placeholder="Qtd."
                        valor={r.quantidade != null ? String(r.quantidade) : ''}
                        onChange={(v) =>
                          setRecurso(i, 'quantidade', v === '' ? null : paraConta(v))
                        }
                      />
                      <input
                        className="form-control"
                        placeholder="Observação (opcional)"
                        value={r.observacao ?? ''}
                        onChange={(e) => setRecurso(i, 'observacao', e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        aria-label="Remover item"
                        onClick={() => rmRecurso(i)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Adicionar item (digita + Enter ou botão +) */}
                <div className="d-flex gap-2">
                  <input
                    className="form-control"
                    list="catalogo-recursos"
                    placeholder="Digite o item (ex.: escada) e clique em +"
                    value={novoRecurso}
                    onChange={(e) => setNovoRecurso(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addRecurso(novoRecurso);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-theme flex-shrink-0"
                    onClick={() => addRecurso(novoRecurso)}
                  >
                    <i className="bi bi-plus-lg"></i>
                  </button>
                </div>
                <datalist id="catalogo-recursos">
                  {catalogoRecursos.map((c) => (
                    <option key={c.id} value={c.nome} />
                  ))}
                </datalist>
              </div>
            </div>
           </fieldset>
          </Tab>
          )}

          {abaVisivel('ambientes') && (
          <Tab data-natureza="composicao" /* medir e fotografar — nasce no escritório, com a planta */ eventKey="ambientes" title="Ambientes">
           <input
             ref={seletorFotoMedida}
             type="file"
             accept="image/*"
             multiple
             className="d-none"
             onChange={(e) => {
               const alvo = alvoFotoMedida.current;
               if (alvo) uploadFotosMedida(alvo.ai, alvo.mi, e.target.files);
               e.target.value = '';
             }}
           />
           <fieldset disabled={!podeEditarAba('ambientes')}>
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">
                    <i className="bi bi-door-open text-theme-1 me-2"></i>
                    Ambientes (cômodos)
                  </h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-theme"
                    onClick={addAmbiente}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar ambiente
                  </button>
                </div>
                <div className="d-flex flex-column gap-3">
                  {ambientes.length === 0 && (
                    <p className="small text-secondary mb-0">
                      Nenhum ambiente. Adicione os cômodos para orientar a instalação.
                    </p>
                  )}
                  {ambientes.map((a, i) => (
                    <div
                      key={i}
                      className="d-flex flex-column gap-3 rounded border p-3"
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="fw-medium">
                          {a.descricao?.trim() || `Ambiente ${i + 1}`}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link btn-square text-danger"
                          onClick={() => rmAmbiente(i)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>

                      <div className="form-floating">
                        <input
                          id={`amb-desc-${i}`}
                          className="form-control"
                          placeholder="Ex.: Quarto casal"
                          value={a.descricao ?? ''}
                          onChange={(e) =>
                            setAmbiente(i, 'descricao', e.target.value)
                          }
                        />
                        <label htmlFor={`amb-desc-${i}`}>Cômodo / descrição</label>
                      </div>

                      {/* Medidas / produtos do ambiente (uma por abertura) */}
                      <div className="d-flex flex-column gap-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <label className="form-label mb-0">Medidas / produtos</label>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-theme"
                            onClick={() => addMedida(i)}
                          >
                            <i className="bi bi-plus-lg me-1"></i>
                            Adicionar medida
                          </button>
                        </div>
                        {a.medidas.length === 0 && (
                          <p className="small text-secondary mb-0">
                            Uma linha por abertura/produto (cada um com sua medida).
                          </p>
                        )}
                        {a.medidas.map((m, mi) => (
                          <div
                            key={mi}
                            className="row g-2 align-items-start rounded bg-light p-3"
                          >
                            <div className="col-12 col-md-3">
                              <input
                                className="form-control"
                                placeholder="Abertura (ex.: Janela 1)"
                                value={m.abertura ?? ''}
                                onChange={(e) =>
                                  setMedida(i, mi, 'abertura', e.target.value)
                                }
                              />
                            </div>
                            <div className="col-12 col-md-3">
                              <input
                                className="form-control"
                                placeholder="Medida (ex.: 2,35 x 1,90)"
                                value={m.medida ?? ''}
                                onChange={(e) =>
                                  setMedida(i, mi, 'medida', e.target.value)
                                }
                              />
                            </div>
                            <div className="col-12 col-md-3">
                              <input
                                className="form-control"
                                placeholder="Modelo / produto"
                                value={m.modelo ?? ''}
                                onChange={(e) =>
                                  setMedida(i, mi, 'modelo', e.target.value)
                                }
                              />
                            </div>
                            <div className="col-10 col-md-2">
                              <input
                                className="form-control"
                                placeholder="Fixação"
                                value={m.fixacao ?? ''}
                                onChange={(e) =>
                                  setMedida(i, mi, 'fixacao', e.target.value)
                                }
                              />
                            </div>
                            <div className="col-2 col-md-1 d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-link btn-square text-danger"
                                onClick={() => rmMedida(i, mi)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                            <div className="col-12">
                              <input
                                className="form-control"
                                placeholder="Observação desta medida (opcional)"
                                value={m.obs ?? ''}
                                onChange={(e) =>
                                  setMedida(i, mi, 'obs', e.target.value)
                                }
                              />
                            </div>
                            {/* Fotos desta medida — mostram ao instalador onde o produto vai */}
                            <div className="col-12 d-flex flex-column gap-2">
                              <label className="form-label small text-secondary mb-0">
                                Fotos desta medida
                              </label>
                              <div className="d-flex flex-wrap gap-3">
                                {m.fotos.map((f, fi) => (
                                  <div
                                    key={fi}
                                    className="position-relative rounded overflow-hidden border"
                                    style={{ width: '6rem', height: '6rem' }}
                                  >
                                    <img
                                      src={`/uploads/${f.arquivo}`}
                                      alt={f.nome ?? 'foto'}
                                      className="w-100 h-100 object-fit-cover"
                                      style={{ cursor: 'zoom-in' }}
                                      onClick={() =>
                                        setLightboxSrc(`/uploads/${f.arquivo}`)
                                      }
                                    />
                                    {/* Tirar foto é alterar a medida: só quem edita
                                        vê o ✕ — botão morto na tela é promessa falsa. */}
                                    {podeEditarAba('ambientes') && (
                                    <button
                                      type="button"
                                      onClick={() => rmFotoMedida(i, mi, fi)}
                                      className="btn btn-sm position-absolute top-0 end-0 m-1 d-flex align-items-center justify-content-center rounded-circle text-white p-0"
                                      style={{
                                        width: '1.4rem',
                                        height: '1.4rem',
                                        background: 'rgba(0,0,0,.6)',
                                      }}
                                      aria-label="Remover foto"
                                    >
                                      <i className="bi bi-x-lg" style={{ fontSize: '.7rem' }}></i>
                                    </button>
                                    )}
                                  </div>
                                ))}
                                {/* Um <div>, não <label>+<input>: dentro do fieldset
                                    travado o input não abre, e o div abre o seletor
                                    que mora fora dele. */}
                                {(podeEditarAba('ambientes') ||
                                  pode('ordens-servico.ambientes.enviar')) && (
                                <div
                                  role="button"
                                  tabIndex={0}
                                  aria-disabled={uploadingMedida === `${i}:${mi}`}
                                  className="d-flex flex-column align-items-center justify-content-center gap-1 rounded border border-dashed text-secondary small"
                                  style={{ width: '6rem', height: '6rem', cursor: 'pointer' }}
                                  onClick={() => {
                                    if (uploadingMedida) return;
                                    alvoFotoMedida.current = { ai: i, mi };
                                    seletorFotoMedida.current?.click();
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    if (uploadingMedida) return;
                                    alvoFotoMedida.current = { ai: i, mi };
                                    seletorFotoMedida.current?.click();
                                  }}
                                >
                                  {uploadingMedida === `${i}:${mi}` ? (
                                    <span className="spinner-border spinner-border-sm" />
                                  ) : (
                                    <>
                                      <i className="bi bi-image fs-5"></i>
                                      Adicionar
                                    </>
                                  )}
                                </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="form-floating">
                        <textarea
                          id={`amb-obs-${i}`}
                          className="form-control"
                          style={{ height: '80px' }}
                          placeholder="Detalhes da instalação deste ambiente…"
                          value={a.obs ?? ''}
                          onChange={(e) => setAmbiente(i, 'obs', e.target.value)}
                        />
                        <label htmlFor={`amb-obs-${i}`}>
                          Observações do ambiente (instruções gerais)
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
           </fieldset>
          </Tab>
          )}

          {/* Visitas, Execução, Autorização e Atividades já têm permissão
              própria em OUTRO módulo ou recurso — aqui só se decide se a aba
              aparece; o que se pode fazer dentro dela é a rota que diz. */}
          {abaVisivel('visitas') && (
          <Tab data-natureza="composicao" /* marcar as visitas */ eventKey="visitas" title="Visitas">
            {!edicao ? (
              <div className="card adminuiux-card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">
                    <i className="bi bi-calendar-check text-theme-1 me-2"></i>
                    Visitas
                  </h6>
                  <p className="small text-secondary mb-0">
                    Salve a OS para agendar visitas.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* VISITAS SÃO CONSULTA AQUI (29/10/2026, decisão dele). Marcar,
                    mudar e apagar visita é na Agenda — a tela feita para isso, e
                    a que confere a loja no servidor. Na OS a lista é registro:
                    o vendedor e o instalador veem quando e com quem. O botão
                    leva à agenda da loja da OS, para quem agenda lá. */}
                {/* Visitas agendadas */}
                <div className="card adminuiux-card mb-3">
                  <div className="card-body">
                    <h6 className="mb-3 d-flex align-items-center gap-2">
                      <span>
                        <i className="bi bi-calendar-check text-theme-1 me-2"></i>
                        Visitas agendadas
                      </span>
                      {visitas.length > 0 && (
                        <span className="badge text-bg-secondary">{visitas.length}</span>
                      )}
                      {lojaDaOS &&
                        usuario?.lojas_agendamentos?.some((l) => l.codigo === lojaDaOS.codigo) && (
                          <Link
                            to={`/agenda/agendamentos/loja/${lojaDaOS.codigo.toLowerCase()}`}
                            className="btn btn-sm btn-outline-theme ms-auto"
                          >
                            <i className="bi bi-calendar-plus me-1"></i>
                            Agendar na agenda
                          </Link>
                        )}
                    </h6>
                    {/* Tabela do tema (mesmo padrão de perfis-acessos): uma ida por
                        linha, com o expansor à ESQUERDA. Fechada mostra o que
                        identifica a visita; aberta, o que se mexe — a PAUTA primeiro,
                        porque é o texto que vai pro roteiro e muda até a véspera. */}
                    {visitas.length === 0 ? (
                      <p className="small text-secondary mb-0">
                        Nenhuma visita agendada.
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table align-middle table-hover mb-0">
                          <thead>
                            <tr className="text-secondary small text-uppercase">
                              <th style={{ width: '2.5rem' }}></th>
                              <th style={{ minWidth: '6rem' }}>Data</th>
                              <th style={{ minWidth: '4rem' }}>Hora</th>
                              <th style={{ minWidth: '9rem' }}>Quem vai</th>
                              <th style={{ minWidth: '7rem' }}>Status</th>
                              <th>O que fazer</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visitas.map((v, i) => {
                              const chave = v.id ?? -(i + 1);
                              const aberto = visitasAbertas.has(chave);
                              return (
                                <Fragment key={chave}>
                                  <tr>
                                    <td className="pe-0">
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 text-decoration-none"
                                        onClick={() => toggleVisitaAberta(chave)}
                                        title={aberto ? 'Fechar' : 'Abrir'}
                                      >
                                        <i
                                          className={`bi ${
                                            aberto
                                              ? 'bi-chevron-down'
                                              : 'bi-chevron-right'
                                          } text-secondary`}
                                        ></i>
                                      </button>
                                    </td>
                                    <td className="fw-medium">
                                      {v.data ? formatarDiaMes(v.data) : '—'}
                                    </td>
                                    <td>{v.hora || '—'}</td>
                                    <td className="small">
                                      {v.instaladores.length > 0
                                        ? v.instaladores.map((x) => x.nome).join(', ')
                                        : '—'}
                                    </td>
                                    <td>
                                      <span className="badge text-bg-light">
                                        {STATUS_VISITA.find(
                                          (s) => s.value === v.status,
                                        )?.label ?? v.status}
                                      </span>
                                    </td>
                                    <td className="small text-secondary">
                                      {v.pauta || '—'}
                                    </td>
                                  </tr>
                                  {aberto && (
                                    <tr className="table-light">
                                      <td></td>
                                      <td colSpan={5} className="pb-3 small">
                                        <div className="mb-1">
                                          <span className="text-secondary">O que fazer: </span>
                                          {v.pauta || '—'}
                                        </div>
                                        <div>
                                          <span className="text-secondary">Como terminou: </span>
                                          {v.desfecho || '—'}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </Tab>
          )}

          {abaVisivel('execucao') && (
          <Tab data-natureza="campo" /* o instalador registrando o que fez */ eventKey="execucao" title="Execução">
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0 d-flex align-items-center gap-2">
                    <span>
                      <i className="bi bi-hammer text-theme-1 me-2"></i>
                      Execução
                    </span>
                    {execTravada && (
                      <span className="badge text-bg-secondary">
                        <i className="bi bi-lock me-1"></i>
                        Travada
                      </span>
                    )}
                  </h6>
                  {edicao && podeDestravarExec && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-theme"
                      onClick={() => (execTravada ? alternarTrava() : setConfirmar('travar'))}
                      disabled={travando}
                    >
                      {travando ? (
                        <span className="spinner-border spinner-border-sm me-1" />
                      ) : execTravada ? (
                        <i className="bi bi-unlock me-1"></i>
                      ) : (
                        <i className="bi bi-lock me-1"></i>
                      )}
                      {execTravada ? 'Destravar' : 'Travar'}
                    </button>
                  )}
                </div>
                <div className="d-flex flex-column gap-3">
                  {!edicao ? (
                    <p className="small text-secondary mb-0">
                      Salve a OS para registrar a execução.
                    </p>
                  ) : (
                    <>
                      {execTravada && (
                        <p className="small text-secondary d-flex align-items-center gap-2 rounded bg-light p-2 mb-0">
                          <i className="bi bi-lock flex-shrink-0"></i>
                          Execução travada
                          {travaInfo.por ? ` por ${travaInfo.por}` : ''}
                          {travaInfo.dt ? ` em ${formatarDataHora(travaInfo.dt)}` : ''} — os
                          instaladores não podem mais editar.
                        </p>
                      )}

                      <div className="d-flex flex-column gap-3">
                        {execucoes.length === 0 && (
                          <p className="small text-secondary mb-0">
                            Nenhum lançamento ainda.
                          </p>
                        )}
                        {execucoes.map((e) => {
                          const meu = usuario?.id === e.usuario_id;
                          const podeApagar = (meu && podeEscreverExec) || podeDestravarExec;
                          return (
                            <div
                              key={e.id}
                              className="d-flex gap-2 border-bottom pb-3"
                            >
                              {e.usuario_foto ? (
                                <img
                                  src={e.usuario_foto}
                                  alt={e.usuario_nome ?? ''}
                                  className="rounded-circle object-fit-cover flex-shrink-0"
                                  style={{ width: '2rem', height: '2rem' }}
                                />
                              ) : (
                                <span
                                  className="rounded-circle bg-light d-flex align-items-center justify-content-center small text-secondary flex-shrink-0"
                                  style={{ width: '2rem', height: '2rem' }}
                                >
                                  {(e.usuario_nome ?? '?').charAt(0).toUpperCase()}
                                </span>
                              )}
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2">
                                  <span className="fw-medium">
                                    {e.usuario_nome ?? 'Usuário'}
                                  </span>
                                  <span className="small text-secondary">
                                    {e.criado_em ? formatarDataHora(e.criado_em) : ''}
                                  </span>
                                  {podeApagar && (
                                    <button
                                      type="button"
                                      onClick={() => apagarExecucao(e.id)}
                                      className="btn btn-sm btn-link btn-square text-secondary ms-auto p-0"
                                      aria-label="Apagar lançamento"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  )}
                                </div>
                                {e.texto && (
                                  <p
                                    className="mb-0"
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {e.texto}
                                  </p>
                                )}
                                {e.fotos.length > 0 && (
                                  <div className="d-flex flex-wrap gap-2 mt-2">
                                    {e.fotos.map((f, i) => (
                                      <img
                                        key={i}
                                        src={`/uploads/${f.arquivo}`}
                                        alt={f.nome ?? 'foto'}
                                        className="rounded object-fit-cover border"
                                        style={{ width: '5rem', height: '5rem', cursor: 'zoom-in' }}
                                        onClick={() =>
                                          setLightboxSrc(`/uploads/${f.arquivo}`)
                                        }
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {podeEscreverExec ? (
                        <div className="border-top pt-3 d-flex flex-column gap-2">
                          <textarea
                            className="form-control"
                            style={{ height: '80px' }}
                            value={execTexto}
                            onChange={(e) => setExecTexto(e.target.value)}
                            placeholder="O que foi feito, defeito encontrado, identificação do produto…"
                          />
                          <div className="d-flex flex-wrap gap-2">
                            {execFotos.map((f, i) => (
                              <div
                                key={i}
                                className="position-relative rounded overflow-hidden border"
                                style={{ width: '5rem', height: '5rem' }}
                              >
                                <img
                                  src={`/uploads/${f.arquivo}`}
                                  alt={f.nome ?? 'foto'}
                                  className="w-100 h-100 object-fit-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExecFotos((arr) => arr.filter((_, k) => k !== i))
                                  }
                                  className="btn btn-sm position-absolute top-0 end-0 m-1 d-flex align-items-center justify-content-center rounded-circle text-white p-0"
                                  style={{
                                    width: '1.4rem',
                                    height: '1.4rem',
                                    background: 'rgba(0,0,0,.6)',
                                  }}
                                  aria-label="Remover foto"
                                >
                                  <i className="bi bi-x-lg" style={{ fontSize: '.7rem' }}></i>
                                </button>
                              </div>
                            ))}
                            <label
                              className="d-flex flex-column align-items-center justify-content-center gap-1 rounded border border-dashed text-secondary small"
                              style={{ width: '5rem', height: '5rem', cursor: 'pointer' }}
                            >
                              {uploadingExec ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <>
                                  <i className="bi bi-image fs-5"></i>
                                  Foto
                                </>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                className="d-none"
                                disabled={uploadingExec}
                                onChange={(e) => {
                                  uploadExecFotos(e.target.files);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          </div>
                          <div>
                            <button
                              type="button"
                              className="btn btn-theme"
                              onClick={enviarExecucao}
                              disabled={enviandoExec || uploadingExec}
                            >
                              {enviandoExec ? (
                                <span className="spinner-border spinner-border-sm me-1" />
                              ) : (
                                <i className="bi bi-plus-lg me-1"></i>
                              )}
                              Adicionar lançamento
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="small text-secondary border-top pt-3 mb-0">
                          Execução travada — somente leitura.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </Tab>
          )}

          {abaVisivel('autorizacao') && (
          <Tab data-natureza="consulta" /* ver e aprovar — o caso da mãe dele */ eventKey="autorizacao" title="Autorização">
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">
                    <i className="bi bi-patch-check text-theme-1 me-2"></i>
                    Autorizações
                  </h6>
                  {/* Ver a aba (`pedido.ver`) não é poder pedir: o POST pede `pedido.abrir`. */}
                  {edicao && pode('autorizacoes.pedido.abrir') && (
                    <button
                      type="button"
                      className="btn btn-sm btn-theme"
                      onClick={() =>
                        navigate(
                          `/autorizacoes/nova?os=${id}&osLabel=${encodeURIComponent(
                            codigoOS,
                          )}&voltar=${encodeURIComponent(
                            `/ordens-servico/${id}?tab=autorizacao`,
                          )}`,
                        )
                      }
                    >
                      <i className="bi bi-plus-lg me-1"></i> Nova autorização
                    </button>
                  )}
                </div>

                {!edicao ? (
                  <p className="small text-secondary mb-0">
                    Salve a OS para poder criar autorizações.
                  </p>
                ) : autsOS.length === 0 ? (
                  <p className="small text-secondary mb-0">
                    Nenhuma autorização para esta OS ainda.
                  </p>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {autsOS.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        className="d-flex align-items-center justify-content-between gap-2 border rounded p-2 bg-transparent text-start w-100"
                        onClick={() =>
                          navigate(
                            `/autorizacoes/${a.id}?voltar=${encodeURIComponent(
                              `/ordens-servico/${id}?tab=autorizacao`,
                            )}`,
                          )
                        }
                      >
                        <span className="text-truncate">
                          <span
                            className={`badge ${badge(
                              ST_AUT[a.status]?.variante ?? 'secondary',
                            )} me-2`}
                          >
                            {ST_AUT[a.status]?.label ?? a.status}
                          </span>
                          {a.titulo}
                        </span>
                        <span className="text-secondary small text-nowrap">
                          {a.tipo_nome ?? ''}
                          {a.qtd_mensagens > 0 ? ` · ${a.qtd_mensagens} msg` : ''}
                          <i className="bi bi-chevron-right ms-2"></i>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Tab>
          )}

          {abaVisivel('chat') && (
          <Tab
            data-natureza="campo" /* perguntar e responder de onde se estiver */
            eventKey="chat"
            title={
              <span>
                Chat
                {edicao && id && (
                  <CustomMensagensBadge ancora={{ tipo: 'ordens_servico', id: Number(id) }} />
                )}
              </span>
            }
          >
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                {!edicao || !id ? (
                  <p className="small text-secondary mb-0">
                    Salve a OS para conversar sobre ela.
                  </p>
                ) : aba === 'chat' && (
                  /* Salas ancoradas nesta OS. Menção, anexo, não lidas, voz e o
                     aviso saem do motor — esta tela só diz em que item está.
                     Monta SÓ com a aba aberta: montada escondida, ela marcava
                     a conversa como lida — a Luciana abriu a aba Dados e as 20
                     não lidas zeraram (ensaio de 29/10/2026). */
                  <CustomMensagensDoItem
                    ancora={{ tipo: 'ordens_servico', id: Number(id) }}
                    tituloSugerido={codigoOS ? `OS ${codigoOS}` : undefined}
                    /* Uma conversa por OS, aberta a todos que estão nela, e que
                       só nasce na primeira mensagem (29/10/2026). */
                    chatUnico
                    nasceNaPrimeira
                  />
                )}
              </div>
            </div>
          </Tab>
          )}

          {abaVisivel('atividades') && (
          <Tab data-natureza="consulta" /* ler o histórico */ eventKey="atividades" title="Atividades">
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <h6 className="mb-3">
                  <i className="bi bi-clock-history text-theme-1 me-2"></i>
                  Atividades
                </h6>
                {!edicao ? (
                  <p className="small text-secondary mb-0">
                    Salve a OS para ver o histórico de atividades.
                  </p>
                ) : atividades.length === 0 ? (
                  <p className="small text-secondary mb-0">
                    Nenhuma atividade registrada ainda.
                  </p>
                ) : (
                  <div className="d-flex flex-column">
                    {atividades.map((ev, i) => (
                      <div key={ev.id} className="d-flex gap-3">
                        {/* trilho + marcador */}
                        <div className="d-flex flex-column align-items-center">
                          <span
                            className="d-flex align-items-center justify-content-center rounded-circle bg-light flex-shrink-0"
                            style={{ width: '2rem', height: '2rem' }}
                          >
                            <IconeEvento tipo={ev.tipo} />
                          </span>
                          {i < atividades.length - 1 && (
                            <span
                              className="flex-grow-1 bg-secondary-subtle my-1"
                              style={{ width: '1px' }}
                            />
                          )}
                        </div>
                        <div className="pb-3">
                          <p className="mb-0">{ev.descricao}</p>
                          {/* O ANTES E O DEPOIS, quando o evento traz (`js_dados`).
                              Até 24/08 este campo nunca era preenchido: a timeline
                              dizia "Editou a OS" e ninguém sabia o quê. */}
                          <DiffDoEvento dados={ev.dados} />
                          <span className="small text-secondary">
                            {ev.usuario_nome ?? 'Sistema'}
                            {ev.criado_em ? ` · ${formatarDataHora(ev.criado_em)}` : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Tab>
          )}

          {abaVisivel('observacoes') && (
          <Tab data-natureza="consulta" /* ler e anotar */ eventKey="observacoes" title="Observações">
           <fieldset disabled={!podeEditarAba('observacoes')}>
            {/* Observações */}
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <h6 className="mb-3">
                  <i className="bi bi-journal-text text-theme-1 me-2"></i>
                  Observações
                </h6>
                <div className="d-flex flex-column gap-3">
                  <CampoObs
                    label="Observação da OS (interna do time)"
                    value={form.observacoes}
                    onChange={(v) => set('observacoes', v)}
                    baseHeight={200}
                    placeholder="Anotações da nossa equipe sobre esta OS…"
                  />
                  <CampoObs
                    label="Observações do pedido (Tiny)"
                    value={form.obs_tiny}
                    onChange={(v) => set('obs_tiny', v)}
                    baseHeight={110}
                    placeholder="Preenchido ao puxar do ERP"
                  />
                  <CampoObs
                    label="Observações internas (Tiny)"
                    value={form.obs_interna_tiny}
                    onChange={(v) => set('obs_interna_tiny', v)}
                    baseHeight={130}
                    placeholder="Preenchido ao puxar do ERP"
                  />
                </div>
              </div>
            </div>
           </fieldset>
          </Tab>
          )}
        </Tabs>

        <Modal className="casaa-modal" show={confirmar != null} onHide={() => setConfirmar(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title className="h6 mb-0">
              {confirmar === 'arquivar' ? 'Arquivar a OS' : 'Travar a execução'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {confirmar === 'arquivar' ? (
              <>
                A OS <strong>{codigoOS}</strong> sai da lista de ativas e da agenda dos
                instaladores. Dá para desarquivar depois, na aba Arquivadas.
              </>
            ) : (
              <>
                Depois de travada, os instaladores não lançam mais nada na execução da
                OS <strong>{codigoOS}</strong> até alguém destravar.
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-link" onClick={() => setConfirmar(null)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-theme"
              onClick={() => {
                const acao = confirmar;
                setConfirmar(null);
                if (acao === 'arquivar') arquivarOS();
                else if (acao === 'travar') alternarTrava();
              }}
            >
              {confirmar === 'arquivar' ? 'Arquivar' : 'Travar'}
            </button>
          </Modal.Footer>
        </Modal>

        <div className="d-flex justify-content-between align-items-center gap-2">
          {/* Arquivar/Desarquivar (Fatia 5b) — à esquerda, só em OS já existente. */}
          {edicao && pode('ordens-servico.os.arquivar') ? (
            arquivada ? (
              <button
                type="button"
                className="btn btn-outline-theme"
                onClick={desarquivarOS}
                disabled={salvando}
                title="OS arquivada — reabrir (destrava os eventos na agenda)"
              >
                <i className="bi bi-arrow-counterclockwise me-1"></i> Desarquivar
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setConfirmar('arquivar')}
                disabled={salvando}
                title="Encerrou de vez — some das listas e trava na agenda (reversível)"
              >
                <i className="bi bi-archive me-1"></i> Arquivar OS
              </button>
            )
          ) : (
            <span />
          )}
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-theme"
              onClick={() => navigate(voltarPara)}
              disabled={salvando}
            >
              Cancelar
            </button>
            {/* Sem nenhuma aba editável, o botão sai — não fica cinza. Botão
                desabilitado promete que um dia dá; ausência diz a verdade: esta
                tela, para esta pessoa, é de leitura. Quem só tem a Execução
                grava por lá, que tem os próprios botões. */}
            {ORDEM_ABAS.some((a) => podeEditarAba(a)) && (
              <button type="submit" className="btn btn-theme" disabled={salvando}>
                {salvando && (
                  <span className="spinner-border spinner-border-sm me-1" />
                )}
                {edicao ? 'Salvar' : 'Criar OS'}
              </button>
            )}
          </div>
        </div>
      </form>

      <Modal className="casaa-modal" show={confirmarPv} onHide={() => setConfirmarPv(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Puxar do ERP de novo?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Isso vai sobrescrever os campos do cliente, endereço, itens, vendedor e
          observações com os dados do pedido — inclusive o que você já editou
          manualmente. Deseja continuar?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setConfirmarPv(false)}
          >
            Cancelar
          </button>
          <button type="button" className="btn btn-theme" onClick={executarPuxar}>
            Sobrescrever
          </button>
        </Modal.Footer>
      </Modal>

      <Lightbox
        open={lightboxSrc !== null}
        close={() => setLightboxSrc(null)}
        slides={lightboxSrc ? [{ src: lightboxSrc }] : []}
      />
    </CustomMolduraPagina>
  );
}

function IconeEvento({ tipo }: { tipo: string }) {
  const mapa: Record<string, { icon: string; cls: string }> = {
    criacao: { icon: 'bi-file-earmark-plus', cls: 'text-theme-1' },
    status: { icon: 'bi-arrow-left-right', cls: 'text-primary' },
    edicao: { icon: 'bi-pencil', cls: 'text-secondary' },
    cobranca: { icon: 'bi-cash-coin', cls: 'text-success' },
    autorizacao: { icon: 'bi-shield-check', cls: 'text-info' },
    trava: { icon: 'bi-lock', cls: 'text-warning' },
    chat: { icon: 'bi-chat-dots', cls: 'text-info' },
    execucao: { icon: 'bi-hammer', cls: 'text-warning' },
  };
  const { icon, cls } = mapa[tipo] ?? {
    icon: 'bi-clock-history',
    cls: 'text-secondary',
  };
  return <i className={`bi ${icon} ${cls}`}></i>;
}

function CampoObs({
  label,
  value,
  onChange,
  placeholder,
  baseHeight = 110,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  baseHeight?: number;
}) {
  const [expandido, setExpandido] = useState(false);
  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex align-items-center justify-content-between">
        <label className="form-label mb-0">{label}</label>
        <button
          type="button"
          className="btn btn-sm btn-link"
          onClick={() => setExpandido((e) => !e)}
        >
          {expandido ? (
            <i className="bi bi-arrows-angle-contract me-1"></i>
          ) : (
            <i className="bi bi-arrows-angle-expand me-1"></i>
          )}
          {expandido ? 'Recolher' : 'Expandir'}
        </button>
      </div>
      <textarea
        className="form-control"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ height: expandido ? '60vh' : `${baseHeight}px` }}
      />
    </div>
  );
}

// Campo: rótulo + controle (helper genérico mantido para paridade com o master).
export function Campo({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`d-flex flex-column gap-1 ${className ?? ''}`}>
      <label className="form-label mb-0">{label}</label>
      {children}
    </div>
  );
}
